// Pure-function helpers for AoE template geometry on a 5e-style grid.
// Mirror of internal/rpgish/game/characteractions/spell_targeting.go;
// the backend is authoritative for damage resolution.

/** @typedef {{col: number, row: number}} GridCell */

/**
 * Returns the set of grid cells inside a cone of `lengthCells`
 * pointing from `apex` toward `endpoint`. Direction is snapped to the
 * nearest of 8 grid directions (N/NE/E/SE/S/SW/W/NW). The apex cell
 * is excluded. Returns [] when the endpoint equals the apex or the
 * length is non-positive.
 *
 * Containment rule (uniform across cardinal and diagonal):
 *   include cell c iff
 *     chebyshev(apex, c) > 0 AND
 *     chebyshev(apex, c) <= lengthCells AND
 *     dot(c - apex, dir) >= 0.5 * chebyshev(apex, c)
 *
 * @param {GridCell} apex
 * @param {GridCell} endpoint
 * @param {number} lengthCells
 * @returns {Array<GridCell>}
 */
export function getConeCells(apex, endpoint, lengthCells) {
  if (lengthCells <= 0) return [];
  const dx = endpoint.col - apex.col;
  const dy = endpoint.row - apex.row;
  if (dx === 0 && dy === 0) return [];

  const dirCol = Math.sign(dx);
  const dirRow = Math.sign(dy);

  /** @type {Array<GridCell>} */
  const cells = [];
  for (let dc = -lengthCells; dc <= lengthCells; dc++) {
    for (let dr = -lengthCells; dr <= lengthCells; dr++) {
      const cheb = Math.max(Math.abs(dc), Math.abs(dr));
      if (cheb === 0 || cheb > lengthCells) continue;
      const dot = dc * dirCol + dr * dirRow;
      if (dot * 2 >= cheb) {
        cells.push({ col: apex.col + dc, row: apex.row + dr });
      }
    }
  }
  return cells;
}

/**
 * Returns every cell within Chebyshev distance `radius` of `center`,
 * including the center cell itself. Used by self-origin AoE targeting
 * to bound the cells the player may hover to aim the template.
 * @param {GridCell} center
 * @param {number} radius
 * @returns {Array<GridCell>}
 */
export function chebyshevDisk(center, radius) {
  if (radius < 0) return [];
  /** @type {Array<GridCell>} */
  const cells = [];
  for (let dc = -radius; dc <= radius; dc++) {
    for (let dr = -radius; dr <= radius; dr++) {
      cells.push({ col: center.col + dc, row: center.row + dr });
    }
  }
  return cells;
}
